import { useMutation } from '@apollo/client';
import { useState } from 'react';
import {
    BudgetFieldsFragment,
    BudgetPurpose,
    UpdateBudgetDocument,
} from '../../graphql/generated';
import { sum } from '../../util/arrays';
import { useBudgetedAmountForPurpose } from './budgetPurpose';
import { BudgetCategoryGroupWithAverage } from './types';

export const useBudgetExpensesFormProps = ({
    budget,
    budgetCategoryGroups,
    budgetPurpose,
    onComplete,
    onError,
}: {
    budget: BudgetFieldsFragment;
    budgetCategoryGroups: BudgetCategoryGroupWithAverage[];
    budgetPurpose: Extract<BudgetPurpose, 'fixed' | 'lifestyle'>;
    onComplete: VoidFunction;
    onError: (error: unknown) => void;
}) => {
    const averageAmountSum = sum(budgetCategoryGroups, (g) => g.average);
    const budgetedAmount = useBudgetedAmountForPurpose({
        budget,
        budgetPurpose,
    });
    const [amount, setAmount] = useState(
        Math.max(0, budgetedAmount || averageAmountSum)
    );
    const [updateBudget, { loading: isSubmitting }] =
        useMutation(UpdateBudgetDocument);

    const handleSubmit = async () => {
        try {
            await updateBudget({
                variables: {
                    input: {
                        id: budget.id,
                        patch: {
                            ...(budgetPurpose === 'fixed' && {
                                fixedAmount: amount,
                            }),
                            ...(budgetPurpose === 'lifestyle' && {
                                lifestyleAmount: amount,
                            }),
                        },
                    },
                },
            });

            onComplete();
        } catch (e) {
            onError(e);
        }
    };

    return {
        handleSubmit,
        isSubmitting,
        amount,
        setAmount,
    };
};
